import React, { CSSProperties, useContext, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { PictureOutlined } from '@ant-design/icons';

import {
  Card,
  Empty,
  Result,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';

import {
  GetClientOrganizationDetailsQuery,
  useGetClientOrganizationDetailsQuery,
} from '../graphql/generated';
import { UserContext } from '../contexts/UserContext';

type Organization = NonNullable<
  NonNullable<GetClientOrganizationDetailsQuery['users_by_pk']>
>['client_organization'];
type OrganizationNetwork = NonNullable<Organization>['organization_networks'][0];
type Network = OrganizationNetwork['network'];

const NO_ACO_KEY = 'no-aco';

interface AcoGroup {
  key: string;
  dc_id: string | null;
  aco_name: string | null;
  aco_phone: string | null;
  aco_url: string | null;
  aco_color: string | null;
  aco_address: string | null;
  aco_provider_contact: string | null;
  logo: string | null;
  networks: Network[];
}

const pageStyle: CSSProperties = {
  maxWidth: 960,
};

const contentCardStyle: CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
};

const organizationHeaderStyle: CSSProperties = {
  alignItems: 'flex-start',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 24,
  justifyContent: 'space-between',
};

const organizationCardGridStyle: CSSProperties = {
  display: 'grid',
  gap: '18px 32px',
  gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1.4fr) minmax(100px, auto)',
};

const acoFieldGridStyle: CSSProperties = {
  display: 'grid',
  gap: '8px 24px',
  gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1.4fr) minmax(100px, auto)',
  marginTop: 10,
};

const acoColumnStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const logoPanelStyle: CSSProperties = {
  alignItems: 'center',
  background: '#f8fafc',
  border: '1px dashed #d9d9d9',
  borderRadius: 8,
  display: 'flex',
  height: 40,
  justifyContent: 'center',
  marginTop: 4,
  width: 64,
};

const getCompactLabelValue = (label: string, value?: React.ReactNode) => {
  return (
    <div>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {label}
      </Typography.Text>
      <div style={{ fontSize: 14, marginTop: 0, minHeight: 20 }}>{value}</div>
    </div>
  );
};

const getLabelValue = (label: string, value?: React.ReactNode) => {
  if (value === undefined || value === null || value === '') return null;

  return (
    <div>
      <Typography.Text type="secondary">{label}</Typography.Text>
      <div style={{ fontSize: 16, marginTop: 2 }}>{value}</div>
    </div>
  );
};

const AcoCard: React.FC<{ aco: AcoGroup }> = ({ aco }) => (
  <Card bodyStyle={{ padding: '14px 20px 16px' }} style={contentCardStyle}>
    <div style={organizationHeaderStyle}>
      <div>
        <Space align="center" size={8}>
          <Typography.Text strong style={{ fontSize: 16 }}>
            {aco.aco_name ||
              (aco.dc_id ? `ACO ${aco.dc_id}` : 'Other Networks')}
          </Typography.Text>
          {aco.dc_id ? <Tag>{aco.dc_id}</Tag> : null}
        </Space>
        {!aco.dc_id ? (
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Networks not associated with an ACO entity
            </Typography.Text>
          </div>
        ) : null}
      </div>
    </div>

    {aco.dc_id ? (
      <div style={acoFieldGridStyle}>
        <div style={acoColumnStyle}>
          {getCompactLabelValue('Address', aco.aco_address)}
          {getCompactLabelValue(
            'Providers Contact',
            aco.aco_provider_contact ? (
              aco.aco_provider_contact.includes('@') ? (
                <Typography.Link href={`mailto:${aco.aco_provider_contact}`}>
                  {aco.aco_provider_contact}
                </Typography.Link>
              ) : (
                aco.aco_provider_contact
              )
            ) : null
          )}
        </div>
        <div style={acoColumnStyle}>
          {getCompactLabelValue('Phone (for patient inquiries)', aco.aco_phone)}
          {getCompactLabelValue(
            'Website',
            aco.aco_url ? (
              <Typography.Link
                href={
                  aco.aco_url.startsWith('http')
                    ? aco.aco_url
                    : `https://${aco.aco_url}`
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                {aco.aco_url}
              </Typography.Link>
            ) : null
          )}
        </div>
        <div style={acoColumnStyle}>
          {getCompactLabelValue(
            'Logo',
            <div style={logoPanelStyle}>
              {aco.logo ? (
                <img
                  alt={`${aco.aco_name || aco.dc_id} logo`}
                  src={aco.logo}
                  style={{ maxHeight: 32, maxWidth: 54, objectFit: 'contain' }}
                />
              ) : (
                <PictureOutlined style={{ color: '#bfbfbf', fontSize: 20 }} />
              )}
            </div>
          )}
          {getCompactLabelValue(
            'Color',
            aco.aco_color ? (
              <Space align="center" size={6}>
                <span
                  style={{
                    background: aco.aco_color,
                    border: '1px solid #e5e7eb',
                    borderRadius: 3,
                    display: 'inline-block',
                    height: 12,
                    width: 12,
                  }}
                />
                <span>{aco.aco_color}</span>
              </Space>
            ) : null
          )}
        </div>
      </div>
    ) : null}

    <div style={{ marginTop: 10 }}>
      {aco.dc_id ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Networks
        </Typography.Text>
      ) : null}
      <div style={{ marginTop: aco.dc_id ? 4 : 0 }}>
        <Space wrap size={4}>
          {aco.networks.map((network, index) => {
            const isPrimaryNetwork = index === 0 && Boolean(aco.dc_id);
            const networkTag = (
              <Tag
                key={network.network_id}
                style={{
                  background: isPrimaryNetwork ? '#e6f7ff' : undefined,
                  border: isPrimaryNetwork ? '2px solid #1890ff' : undefined,
                  borderRadius: 12,
                  fontWeight: isPrimaryNetwork ? 500 : undefined,
                }}
              >
                {network.network_name}
                {aco.dc_id ? ` (${aco.dc_id})` : ''}
              </Tag>
            );

            return isPrimaryNetwork ? (
              <Tooltip
                key={network.network_id}
                title={`Primary network for ${aco.dc_id}`}
              >
                {networkTag}
              </Tooltip>
            ) : (
              networkTag
            );
          })}
        </Space>
      </div>
    </div>
  </Card>
);

const ClientOrganizationProfilePage: React.FC = () => {
  const { user } = useContext(UserContext);
  const { data, loading, error } = useGetClientOrganizationDetailsQuery({
    variables: { user_id: user?.user_id },
  });
  const organization = data?.users_by_pk?.client_organization;
  const organizationNetworks = organization?.organization_networks;

  const activeNetworks = useMemo(() => {
    return (
      organizationNetworks
        ?.filter((organizationNetwork) => {
          return (
            !organizationNetwork.is_archived &&
            !organizationNetwork.network.is_archived
          );
        })
        .map(({ network }) => network)
        // Oldest network first — it is the source of each ACO's variables below
        .sort(
          (a, b) =>
            new Date(a.created_at).getTime() -
              new Date(b.created_at).getTime() ||
            a.network_id.localeCompare(b.network_id)
        ) || []
    );
  }, [organizationNetworks]);

  const acoGroups = useMemo(() => {
    const groups = new Map<string, AcoGroup>();

    for (const network of activeNetworks) {
      const key = network.dc_id || NO_ACO_KEY;
      const existingGroup = groups.get(key);
      if (existingGroup) {
        existingGroup.networks.push(network);
        continue;
      }
      // All ACO variables come from the group's first (oldest) network only,
      // so they can't mix across networks that share a dc_id
      const mergeVars = network.network_merge_vars || {};
      groups.set(key, {
        key,
        dc_id: network.dc_id || null,
        aco_name: mergeVars.dce_name || null,
        aco_phone: mergeVars.dce_phone || null,
        aco_url: mergeVars.dce_url || null,
        aco_color: mergeVars.dce_color || null,
        aco_address:
          [
            mergeVars.dce_address1,
            mergeVars.dce_address2,
            mergeVars.dce_address3,
          ]
            .filter(Boolean)
            .join(', ') || null,
        aco_provider_contact: mergeVars.dce_provider_contact || null,
        logo: mergeVars.dce_logo || null,
        networks: [network],
      });
    }

    // ACO cards sorted by dc_id, with any no-ACO group last
    return Array.from(groups.values()).sort((a, b) => {
      if (!a.dc_id) return 1;
      if (!b.dc_id) return -1;
      return a.dc_id.localeCompare(b.dc_id);
    });
  }, [activeNetworks]);

  if (loading) return <Skeleton />;
  if (error)
    return (
      <Result
        status="error"
        title="There was an error retrieving organization details"
        subTitle={error?.message}
      />
    );
  if (!organization)
    return (
      <Result
        status="warning"
        title="No organization profile found"
        subTitle="Your account is not attached to a client organization."
      />
    );

  return (
    <>
      <Helmet>
        <title>ACO Entities | Mabel</title>
      </Helmet>
      <div style={pageStyle}>
        <Typography.Title level={2} style={{ marginBottom: 4 }}>
          ACO Entities
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ fontSize: 16 }}>
          The ACOs associated with your organization.
        </Typography.Paragraph>

        <Card
          bodyStyle={{ padding: 24 }}
          style={{ ...contentCardStyle, marginBottom: 24 }}
        >
          <div style={organizationCardGridStyle}>
            <div>
              <Typography.Text type="secondary">Organization</Typography.Text>
              <Typography.Title
                level={4}
                style={{ marginBottom: 0, marginTop: 2 }}
              >
                {organization.organization_name}
              </Typography.Title>
              {organization.organization_description ? (
                <Typography.Paragraph
                  style={{ marginBottom: 0, marginTop: 8, maxWidth: 640 }}
                >
                  {organization.organization_description}
                </Typography.Paragraph>
              ) : null}
            </div>
            {getLabelValue('Organization ID', organization.organization_id)}
            {getLabelValue(
              'ACO Entities',
              acoGroups.filter((group) => group.dc_id).length
            )}
          </div>
        </Card>

        {acoGroups.length ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {acoGroups.map((aco) => (
              <AcoCard aco={aco} key={aco.key} />
            ))}
          </Space>
        ) : (
          <Card style={contentCardStyle}>
            <Empty description="No active networks are attached to this organization." />
          </Card>
        )}

        <Typography.Paragraph
          type="secondary"
          style={{ fontSize: 14, marginBottom: 0, marginTop: 24 }}
        >
          If any of this information is incorrect or needs to be updated, please
          contact <a href="mailto:support@getmabel.com">support@getmabel.com</a>
        </Typography.Paragraph>
      </div>
    </>
  );
};

export default ClientOrganizationProfilePage;
